/**
 * Cross-sell test v2 - locked copy for three offers.
 * @see docs/pfpmaker-cross-sell-test-plan.md
 *
 * Preview (prod): set CROSS_SELL_V2_PREVIEW_OFFER for avatar-list card.
 * Preview (localhost): use the floating dev switcher (bottom-right).
 * Sitewide (prod): set ACTIVE_SITEVIDE_BANNER to xsell_* in PromoBanner.tsx. * Analytics + sticky A/B assignment - next sprint.
 */

export type CrossSellOfferId = "companion" | "looksmax" | "ai-photo-editor";

export interface CrossSellOfferCopy {
  id: CrossSellOfferId;
  /** Future UTM campaign slug */
  campaign: "roleplay" | "looksmax" | "ai-photo-editor";
  /** Copy-test variant key (looksmax copy test). Undefined for legacy offers. */
  variantId?: string;
  /** If set, the CTA navigates here (new tab) instead of opening the coming-soon modal. */
  ctaUrl?: string;
  sitewideDesktop: string;
  sitewideMobile: string;
  avatarCardTitle: string;
  avatarCardSubtitle: string;
  teaseTitle: string;
  teaseBody: string;
}

export const CROSS_SELL_V2_BADGE = "NEW";
export const CROSS_SELL_V2_CTA = "Start now";

export const CROSS_SELL_V2_OFFERS: Record<CrossSellOfferId, CrossSellOfferCopy> = {
  companion: {
    id: "companion",
    campaign: "roleplay",
    sitewideDesktop: "Meet an AI companion who's always there for you",
    sitewideMobile: "Meet your AI companion",
    avatarCardTitle: "Meet your AI companion",
    avatarCardSubtitle: "Always there, texts back, always remembers you",
    teaseTitle: "Almost ready",
    teaseBody: "We're still building your AI companion experience. Come back soon for early bird discounts - early access is opening shortly.",
  },
  looksmax: {
    id: "looksmax",
    campaign: "looksmax",
    sitewideDesktop: "See your honest AI face rating and the glow-up plan",
    sitewideMobile: "See your AI face rating",
    avatarCardTitle: "See your AI face rating",
    avatarCardSubtitle: "Honest AI face rating in seconds + how to level up",
    teaseTitle: "Almost ready",
    teaseBody: "AI face rating and your glow-up plan are on the way. Come back soon for early bird discounts - early access is opening shortly.",
  },
  "ai-photo-editor": {
    id: "ai-photo-editor",
    campaign: "ai-photo-editor",
    sitewideDesktop: "Turn one selfie into viral scroll-stopping AI videos",
    sitewideMobile: "Create viral AI videos from a selfie",
    avatarCardTitle: "Create viral AI videos from a selfie",
    avatarCardSubtitle: "One selfie becomes a scroll-stopping AI video + photopacks",
    teaseTitle: "Almost ready",
    teaseBody: "Turn-one-selfie AI videos are still being polished. Come back soon for early bird discounts - early access is opening shortly.",
  },
};

/** Set to preview one offer in the avatar-list placement. null = hidden. */
export const CROSS_SELL_V2_PREVIEW_OFFER: CrossSellOfferId | null = null;

/* ------------------------------------------------------------------ *
 * Looksmax copy test (Phase 2)
 * Offer is fixed to looksmax; each variant is a format-matched copy set.
 * Only the angle changes - badge, CTA, length, tone held constant.
 * @see EXPERIMENTS.looksmax_copy_v1
 * ------------------------------------------------------------------ */

export type LooksmaxCopyVariantId =
  | "control"
  | "curiosity"
  | "provocation"
  | "identity"
  | "outcome";

export const LOOKSMAX_COPY_VARIANT_IDS: LooksmaxCopyVariantId[] = [
  "control",
  "curiosity",
  "provocation",
  "identity",
  "outcome",
];

/** CTA destination for the looksmax cross-sell (/face-ai hub, opens in new tab). */
export const LOOKSMAX_CTA_URL = "/face-ai";

/**
 * Avatar-grid / headshot-grid cross-sell: A/B stopped; locked to the winning copy.
 * looksmax_copy_v1 sitewide A/B is paused — top bar uses deal banner again.
 */
export const LOOKSMAX_AVATAR_GRID_LOCKED_VARIANT: LooksmaxCopyVariantId = "provocation";

const LOOKSMAX_TEASE_TITLE = "Almost ready";
const LOOKSMAX_TEASE_BODY =
  "AI face rating and your glow-up plan are on the way. Come back soon for early bird discounts - early access is opening shortly.";

/** Short human labels for the dev switcher / reporting. */
export const LOOKSMAX_COPY_VARIANT_LABELS: Record<LooksmaxCopyVariantId, string> = {
  control: "Control · Face rating",
  curiosity: "Curiosity · Face score?",
  provocation: "Provocation · Brutally honest",
  identity: "Identity · Your potential",
  outcome: "Outcome · Rated + plan",
};

/**
 * Copy sets are the marketer's locked Category B lines.
 * @see docs/pfpmaker-cross-sell-test-plan.md (lines 123-153)
 * control = the Phase-1 winning line, left unchanged.
 */
export const LOOKSMAX_COPY_SETS: Record<LooksmaxCopyVariantId, CrossSellOfferCopy> = {
  control: {
    id: "looksmax",
    campaign: "looksmax",
    variantId: "control",
    ctaUrl: LOOKSMAX_CTA_URL,
    sitewideDesktop: "See your honest AI face rating and the glow-up plan",
    sitewideMobile: "See your AI face rating",
    avatarCardTitle: "See your AI face rating",
    avatarCardSubtitle: "Honest AI face rating in seconds + how to level up",
    teaseTitle: LOOKSMAX_TEASE_TITLE,
    teaseBody: LOOKSMAX_TEASE_BODY,
  },
  curiosity: {
    id: "looksmax",
    campaign: "looksmax",
    variantId: "curiosity",
    ctaUrl: LOOKSMAX_CTA_URL,
    sitewideDesktop: "How does your face actually score? Get your honest AI rating in seconds.",
    sitewideMobile: "What's your face score?",
    avatarCardTitle: "Get scored in seconds",
    avatarCardSubtitle: "Jawline, skin, symmetry - the honest breakdown.",
    teaseTitle: LOOKSMAX_TEASE_TITLE,
    teaseBody: LOOKSMAX_TEASE_BODY,
  },
  provocation: {
    id: "looksmax",
    campaign: "looksmax",
    variantId: "provocation",
    ctaUrl: LOOKSMAX_CTA_URL,
    sitewideDesktop: "Are you looksmaxxing, or just guessing? Find out your real score.",
    sitewideMobile: "Rate my face - brutally honest",
    avatarCardTitle: "Rate my face",
    avatarCardSubtitle: "Brutally honest AI score + how to level up.",
    teaseTitle: LOOKSMAX_TEASE_TITLE,
    teaseBody: LOOKSMAX_TEASE_BODY,
  },
  identity: {
    id: "looksmax",
    campaign: "looksmax",
    variantId: "identity",
    ctaUrl: LOOKSMAX_CTA_URL,
    sitewideDesktop: "Your face has untapped potential. See your score and your ceiling.",
    sitewideMobile: "See your looks potential",
    avatarCardTitle: "See your looks potential",
    avatarCardSubtitle: "Your score today - and how high you could go.",
    teaseTitle: LOOKSMAX_TEASE_TITLE,
    teaseBody: LOOKSMAX_TEASE_BODY,
  },
  outcome: {
    id: "looksmax",
    campaign: "looksmax",
    variantId: "outcome",
    ctaUrl: LOOKSMAX_CTA_URL,
    sitewideDesktop: "Get your AI face score - plus the exact glow-up plan to raise it.",
    sitewideMobile: "Get rated + a glow-up plan",
    avatarCardTitle: "How do you rank?",
    avatarCardSubtitle: "Free AI face rating + a personalized glow-up plan.",
    teaseTitle: LOOKSMAX_TEASE_TITLE,
    teaseBody: LOOKSMAX_TEASE_BODY,
  },
};

export const LOOKSMAX_COPY_DEFAULT_VARIANT: LooksmaxCopyVariantId = "control";

/** Prod compile-time preview for localhost-less environments. null = use experiment. */
export const LOOKSMAX_COPY_PREVIEW_VARIANT: LooksmaxCopyVariantId | null = null;

export function isLooksmaxCopyVariantId(value: string | null): value is LooksmaxCopyVariantId {
  return value !== null && (LOOKSMAX_COPY_VARIANT_IDS as string[]).includes(value);
}

export function getLooksmaxCopy(variantId: LooksmaxCopyVariantId): CrossSellOfferCopy {
  return LOOKSMAX_COPY_SETS[variantId];
}

/** In-grid avatar cards every 5th row - same toggle pattern as Peeq promos. */
export const ENABLE_AVATAR_GRID_CROSS_SELL_PROMOS = false;

/** Permanent slot 6 in headshot promo row - same toggle pattern as Peeq. */
export const ENABLE_HEADSHOT_GRID_CROSS_SELL_PROMO = false;

/** Tier-1 geo gate for showing cross-sell (reporting still includes all countries). */
export const CROSS_SELL_V2_TIER1_COUNTRIES = new Set([
  "US",
  "CA",
  "GB",
  "AU",
  "NZ",
  "FR",
  "DE",
  "NL",
  "ES",
  "IT",
  "PL",
]);

export type CrossSellPlacement = "sitewide" | "avatarlist";

export function getCrossSellOffer(offerId: CrossSellOfferId): CrossSellOfferCopy {
  return CROSS_SELL_V2_OFFERS[offerId];
}

export function makeHeadshotGridCrossSellPromo(offerId: CrossSellOfferId) {
  return {
    id: "hs-promo-6",
    baseStyleId: "hs-promo-f",
    title: getCrossSellOffer(offerId).avatarCardTitle,
    prompt: "",
    previewGender: "male" as const,
    tags: ["Professional"],
    pinTop: true,
    isCrossSellPromo: true as const,
  };
}

export function makeAvatarGridCrossSellPromo(avatarRow: number, offerId: CrossSellOfferId) {
  return {
    id: `avatar-xsell-promo-r${avatarRow}`,
    baseStyleId: `avatar-xsell-r${avatarRow}`,
    title: getCrossSellOffer(offerId).avatarCardTitle,
    prompt: "",
    previewGender: "male" as const,
    tags: [] as string[],
    isCrossSellPromo: true as const,
  };
}

/** @deprecated Use makeAvatarGridCrossSellPromo */
export function makeCrossSellAvatarPromoStyle(offerId: CrossSellOfferId) {
  return makeAvatarGridCrossSellPromo(0, offerId);
}

export function isCrossSellSitewideBannerId(
  bannerId: string,
): bannerId is `xsell_${CrossSellOfferId}` {
  return bannerId === "xsell_companion" || bannerId === "xsell_looksmax" || bannerId === "xsell_ai-photo-editor";
}

export function crossSellOfferFromSitewideBannerId(bannerId: `xsell_${CrossSellOfferId}`): CrossSellOfferId {
  return bannerId.replace("xsell_", "") as CrossSellOfferId;
}
